import { useRouter } from 'next/router';
import { memo, useCallback } from 'react';

import { useTrackTeamInviteCtaClicked } from '@air/analytics';
import { Gear, MemberAdd, User } from '@air/next-icons';
import { Badge } from '@air/primitive-badge';
import { DropdownMenuItem } from '@air/primitive-dropdown-menu';
import { useAirModal } from '@air/provider-modal';

import { InviteMembersModal } from '~/components/Modals/InviteMembers/InviteMembersModal';
import { WorkspaceHeaderInformationDropdownTeamInviteNudge } from '~/components/v2/components/workspace/workspace-header/WorkspaceHeaderInformationDropdownTeamInviteNudge';
import { Routes } from '~/constants/routes';
import { useTeamInviteNudgeEligibility } from '~/hooks/useTeamInviteNudgeEligibility';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useWorkspaceMembers } from '~/swr-hooks/members/useWorkspaceMembers';
import { canAddWorkspaceMembers, canSeeWorkspaceMembers } from '~/utils/permissions/workspacePermissions';

export const WorkspaceHeaderInformationDropdownWorkspaceItems = memo(() => {
  const router = useRouter();
  const { data: permissions } = useCurrentWorkspacePermissionsContext();
  const { members } = useWorkspaceMembers();
  const { trackTeamInviteCtaClicked } = useTrackTeamInviteCtaClicked();
  const { isTeamInviteNudgeEligible } = useTeamInviteNudgeEligibility();
  const [showInviteMembersModal] = useAirModal(InviteMembersModal);

  const canViewMembers = canSeeWorkspaceMembers(permissions);
  const canEditMembers = canAddWorkspaceMembers(permissions);

  const handleInviteMembers = useCallback(() => {
    if (isTeamInviteNudgeEligible) {
      trackTeamInviteCtaClicked({ location: 'workspace_settings_dropdown' });
    }

    showInviteMembersModal({ location: 'workspace-dropdown' });
  }, [isTeamInviteNudgeEligible, showInviteMembersModal, trackTeamInviteCtaClicked]);

  const handleWorkspaceSettings = useCallback(() => {
    router.push(Routes.workspaceSettings.members);
  }, [router]);

  return (
    <>
      {isTeamInviteNudgeEligible ? (
        <WorkspaceHeaderInformationDropdownTeamInviteNudge onInviteMembers={handleInviteMembers} />
      ) : (
        canEditMembers && (
          <DropdownMenuItem
            data-id="INVITE_USERS"
            label="Invite users"
            onSelect={handleInviteMembers}
            prefix={<MemberAdd className="size-4" />}
            suffix={
              !!members?.allMembers.length && (
                <Badge appearance="pill" className="group-hover/dropdown-menu-item:bg-grey-5" size="medium">
                  <User className="size-3" />
                  {members.allMembers.length}
                </Badge>
              )
            }
          />
        )
      )}
      {canViewMembers && (
        <DropdownMenuItem
          label="Workspace settings"
          onSelect={handleWorkspaceSettings}
          prefix={<Gear className="size-4" />}
        />
      )}
    </>
  );
});

WorkspaceHeaderInformationDropdownWorkspaceItems.displayName = 'WorkspaceHeaderInformationDropdownWorkspaceItems';
